import * as React from "react";
import { Slot as SlotPrimitive } from "radix-ui";
import { cva, type VariantProps } from "class-variance-authority";

import {
  CONTROL_DENSE_SQUARE_CLASS,
  CONTROL_HEIGHT_CLASSES,
  CONTROL_SQUARE_CLASSES,
} from "@/lib/design-system/control-density";
import { cn } from "@/lib/utils";
import { Spinner } from "@/components/ui/spinner";

const buttonVariants = cva(
  "inline-flex items-center justify-center rounded-control font-medium transition-all disabled:pointer-events-none disabled:!text-disabled-foreground disabled:opacity-100 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:ring-3 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background aria-invalid:ring-destructive/20 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        primary:
          "bg-action-fill text-action-fill-foreground hover:bg-action-fill-hover disabled:!bg-disabled",
        outline:
          "border border-input bg-card disabled:!bg-disabled hover:bg-accent hover:text-accent-foreground aria-pressed:bg-accent aria-pressed:text-accent-foreground max-md:border-transparent max-md:bg-foreground/6 max-md:aria-pressed:bg-action-tint max-md:aria-pressed:text-action-strong",
        ghost:
          "hover:bg-accent hover:text-accent-foreground aria-pressed:bg-accent aria-pressed:text-accent-foreground",
        quiet:
          "text-muted-foreground hover:bg-accent hover:text-foreground aria-pressed:bg-accent aria-pressed:text-accent-foreground aria-[current=page]:bg-accent aria-[current=page]:font-semibold aria-[current=page]:text-accent-foreground",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive-hover focus-visible:ring-destructive disabled:!bg-disabled",
        "destructive-quiet":
          "text-destructive-strong hover:bg-destructive-bg hover:text-destructive-strong focus-visible:ring-destructive",
        link: "text-left text-link underline-offset-4 hover:text-link-hover hover:underline",
        field:
          "justify-start font-normal text-muted-foreground hover:text-foreground",
        chip:
          "rounded-full border border-border bg-surface text-foreground hover:border-border-strong aria-pressed:border-action aria-pressed:bg-action aria-pressed:text-action-foreground aria-pressed:hover:bg-action-hover",
        tint: "bg-action-tint text-action-strong hover:bg-accent",
        selected: "bg-action-fill text-action-fill-foreground disabled:!bg-disabled",
        brand: "bg-brand text-brand-foreground hover:bg-brand-accent disabled:!bg-disabled",
        ink: "bg-action text-action-foreground hover:bg-action-hover active:bg-action-active disabled:!bg-disabled",
        success:
          "bg-success text-success-foreground hover:bg-success-hover focus-visible:ring-success disabled:!bg-disabled",
        inverse:
          "bg-(--paper)/12 text-(--paper) hover:bg-(--paper)/20 aria-pressed:bg-(--paper) aria-pressed:text-(--paper-foreground) disabled:!bg-(--paper)/6 disabled:!text-(--paper)/40",
        "inverse-quiet":
          "text-(--paper)/80 hover:bg-(--paper)/12 hover:text-(--paper)",
      },
      size: {
        default: `${CONTROL_HEIGHT_CLASSES.default} gap-2 px-4 text-sm`,
        sm: `${CONTROL_HEIGHT_CLASSES.sm} gap-1.5 px-3 text-xs`,
        compact: `${CONTROL_HEIGHT_CLASSES.compact} gap-1.5 px-2 text-xs`,
        auto: "h-auto gap-2 px-4 py-2 text-sm",
        bare: "h-auto gap-2 p-0 text-sm",
        "bare-xs": "h-auto gap-1 p-0 text-xs",
        inline: "h-auto gap-1 p-0 text-[length:inherit] leading-[inherit]",
        icon: CONTROL_SQUARE_CLASSES.default,
        "icon-sm": CONTROL_SQUARE_CLASSES.sm,
        "icon-compact": CONTROL_SQUARE_CLASSES.compact,
        "icon-dense": CONTROL_DENSE_SQUARE_CLASS,
        "icon-inline": "size-5 min-h-5 [&_svg:not([class*='size-'])]:size-3",
        "icon-round": "size-[72px] rounded-full [&_svg:not([class*='size-'])]:size-7",
        "icon-round-sm": "size-11 rounded-full [&_svg:not([class*='size-'])]:size-5",
        chip: "min-h-11 gap-2 px-4 text-[0.9375rem]",
        pill: `${CONTROL_HEIGHT_CLASSES.compact} gap-2 rounded-full px-3 text-xs font-semibold`,
      },
    },
    defaultVariants: {
      size: "default",
    },
  },
);

type ButtonProps = React.ComponentProps<"button"> &
  Omit<VariantProps<typeof buttonVariants>, "variant"> & {
    variant: NonNullable<VariantProps<typeof buttonVariants>["variant"]>;
    asChild?: boolean;
    isLoading?: boolean;
  };

function LoadingContent({ children }: { children: React.ReactNode }) {
  return (
    <>
      <span className="inline-flex items-center justify-center gap-[inherit] opacity-0">
        {children}
      </span>
      <Spinner
        role={undefined}
        aria-label={undefined}
        aria-hidden="true"
        className="absolute inset-0 m-auto"
      />
    </>
  );
}

function preventActivation(event: React.MouseEvent) {
  event.preventDefault();
}

function slotContent(children: React.ReactNode, isLoading: boolean) {
  if (
    !isLoading ||
    !React.isValidElement<{
      children?: React.ReactNode;
      onClick?: (event: React.MouseEvent) => void;
    }>(children)
  ) {
    return children;
  }
  return React.cloneElement(
    children,
    { onClick: preventActivation },
    <LoadingContent>{children.props.children}</LoadingContent>,
  );
}

function Button({
  className,
  variant,
  size,
  asChild = false,
  isLoading = false,
  children,
  ...props
}: ButtonProps) {
  const classes = cn(
    buttonVariants({ variant, size, className }),
    isLoading && "relative",
  );

  if (asChild) {
    return (
      <SlotPrimitive.Slot
        data-slot="button"
        className={cn(classes, isLoading && "pointer-events-none")}
        aria-busy={isLoading || undefined}
        aria-disabled={isLoading || undefined}
        {...props}
        {...(isLoading && { tabIndex: -1, onClick: preventActivation })}
      >
        {slotContent(children, isLoading)}
      </SlotPrimitive.Slot>
    );
  }

  return (
    <button
      data-slot="button"
      className={classes}
      aria-busy={isLoading || undefined}
      {...props}
      disabled={props.disabled || isLoading}
    >
      {isLoading ? <LoadingContent>{children}</LoadingContent> : children}
    </button>
  );
}

export { Button, buttonVariants };
